import * as React from "react";
import * as _ from "lodash";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty,
    ComponentElementPropertyType
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import {Static} from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementOverflowType,
    ComponentElementStatic
} from "@bizform/type/model/component_element/ComponentElement.model";
import {DomAttribute} from "@bizform/type/model/dom/DomAttribute.model";
import {WindowCommandbarItem} from "@bizform/type/model/data_set/WindowIconItem.model";
import {OptionalWindowCommandState} from "@bizform/type/model/data_set/LookUp.model";
import {PluginType} from "@bizform/type/model/model/Plugin.model";
import {WindowKey} from "@bizform/type/model/model/WindowKey.model";
import {
    GridIdKeyConstant,
    GridLinkIdKeyConstant
} from "@bizform/type/model/component_element/manifest_constant/Grid.constant";
import {ComponentHelper, Debug, StringHelper} from "@bizform/helper/dist/js/bizflow/helper.min";
import {
    ComponentElementClass,
    I18N,
    ModalHandler,
    ReactComponentElementInit
} from "@bizform/core/dist/js/bizflow/core.min";
import {AbstractComponentElement} from "../bf_component/AbstractComponentElement";
import {I18n} from "../misc/model/I18n.model";
import {GridHelper} from "../bf_grid_handler/helper/Grid.helper";


type Props = ComponentElementOverflowType & {
    title?: string;
    label?: string;
    model?: Array<WindowCommandbarItem>;
    width: string;
    height: string;
    top: string;
    left: string;
    useCommand: boolean;
    useWindowMode: boolean;
    windowState?: OptionalWindowCommandState;
    footer: boolean;
    useResize: boolean;
    minWidth?: string;
    minHeight?: string;
    maxWidth?: string;
    maxHeight?: string;
    [GridIdKeyConstant]?: string;
    [GridLinkIdKeyConstant]?: string;
};

type WrappedCompState = Props & {
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
};

/**
 * ModalPanel component element
 *
 * @since 0.0.1
 * @author Jongkwang Park, Heungsoo Ham
 * @class
 */
@ComponentElementClass()
@Static<ComponentElementStatic<null>>()
export class BfModalPanel extends AbstractComponentElement<Props, State> {
    static readonly pluginType: string = PluginType.MODALHANDLER;

    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_SGF4sTfhXbtK7LU2bUIRd1nop6hS7aH",
        category: ComponentCategory.HANDLER,
        label: I18n.handler.modalPanel.label,
        description: I18n.handler.modalPanel.description,
        icon: "",
        property: null
    };

    focus(): void {
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, null>(
                property,
                this);

            this.setState(this._setBooleanTypeValue(newState));
        } catch (e) {
            Debug.console.error("setProperty", e);
        }
    }

    protected _setBooleanTypeValue(newState: State): State {
        if ("useCommand" in newState.wrappedCompState) {
            newState.wrappedCompState.useCommand = ComponentHelper.toBoolean(newState.wrappedCompState.useCommand);
        } else {
            newState.wrappedCompState.useCommand = false;
        }

        if ("footer" in newState.wrappedCompState) {
            newState.wrappedCompState.footer = ComponentHelper.toBoolean(newState.wrappedCompState.footer);
        } else {
            newState.wrappedCompState.footer = false;
        }

        return newState;
    }

    protected _itemValueEvent(e: {originalEvent: React.MouseEvent<any>, item: WindowCommandbarItem}): void {
        // emit value
        e.originalEvent.preventDefault();
        const itemValue = e.item && (e.item as {itemValue}).itemValue;

        if (itemValue) {
            this.emitFormState(itemValue);
        }
    }

    emitFormState(value: string): void {
    }

    emitHasContainerKey(): void {
    }

    noDisplayInheritance() {
    }

    @ReactComponentElementInit(BfModalPanel)
    static init(): ComponentElement<string> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    protected _ref: HTMLElement;
    protected readonly _internalModel = {
        label: "command icon",
        value: [
            {
                label: OptionalWindowCommandState.DIALOG,
                icon: "bf-icon icon-internal-link-alt",
                description: "",
                itemValue: OptionalWindowCommandState.DIALOG,
                command: function () {}
            }
        ],
        description: "The model for modal panel's command icon bar",
        type: ComponentElementPropertyType.FORMSTATE
    };

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {
                model: [],
                width: "30rem",
                height: "20rem",
                top: "0",
                left: "0",
                useCommand: true,
                useWindowMode: false,
                windowState: OptionalWindowCommandState.DEFAULT,
                footer: true,
                useResize: true,
                minWidth: "0",
                minHeight: "0",
                maxWidth: undefined,
                maxHeight: undefined
            }
        };

        this._setRef = this._setRef.bind(this);
        this._filterWindowIconsByState = this._filterWindowIconsByState.bind(this);
        this._itemValueEvent = this._itemValueEvent.bind(this);
        this._icon = this._icon.bind(this);
        this._commandClick = this._commandClick.bind(this);
        this._commandEnter = this._commandEnter.bind(this);
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        Debug.console.error(error, errorInfo);
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                case ComponentElementDisplayMode.READONLY:
                case ComponentElementDisplayMode.DISABLED:
                case ComponentElementDisplayMode.PRINT:
                    return this._render4ReadOnly();
                default:
                    return null;
            }
        }
    }

    protected _render4ReadOnly(): React.ReactNode {
        const bfContent = {[DomAttribute.MODALCONTENT]: ""};
        const bfBody = {
            [DomAttribute.MODALBODY]: "",
            [DomAttribute.CONTAINER]: "bf-content-container"
        };
        let { width, height } = this.state.wrappedCompState;
        if (width && width.trim().endsWith("%")) {
            width = Math.floor(parseInt(width) / 100 * window.innerWidth) + "px";
        }
        if (height && height.trim().endsWith("%")) {
            height = Math.floor(parseInt(height) / 100 * window.innerHeight) + "px";
        }
        const modalWindowStyle: {
            width: string;
            height?: string;
            minWidth?: string;
            minHeight?: string;
            maxWidth?: string;
            maxHeight?: string;
        } = {
            width: width
        };

        if (!window[WindowKey.DESIGNMODE]) {
            modalWindowStyle.height = height;
            if (this.state.wrappedCompState.maxWidth) {
                modalWindowStyle.maxWidth = this.state.wrappedCompState.maxWidth;
            }
            if (this.state.wrappedCompState.maxHeight) {
                modalWindowStyle.maxHeight = this.state.wrappedCompState.maxHeight;
            }
            if (this.state.wrappedCompState.minWidth) {
                modalWindowStyle.minWidth = this.state.wrappedCompState.minWidth;
            }
            if (this.state.wrappedCompState.minHeight) {
                modalWindowStyle.minHeight = this.state.wrappedCompState.minHeight;
            }
        }
        if (this.state.wrappedCompState.useResize) {
            bfContent[DomAttribute.MODALRESIZE] = "";
        }
        if (this.accessibility && this.state.wrappedCompState.label) {
            bfContent["aria-label"] = this.state.wrappedCompState.label;
        }
        if (this.accessibility) {
            bfContent["role"] = "dialog";
            bfContent["aria-hidden"] = "true";
        }
        if (this.state.wrappedCompState[GridIdKeyConstant]) {
            bfContent[DomAttribute.GRIDID] = this.state.wrappedCompState[GridIdKeyConstant];
            bfContent[DomAttribute.GRIDLINKID] = this.state.wrappedCompState[GridLinkIdKeyConstant];
            bfContent[DomAttribute.GRIDCOMPONENT] = "_0ofi1xMLbJL4an4UyiCzDVcRWpGX0Xi";
            bfBody[DomAttribute.GRIDBODYCONTAINER] = StringHelper.elementId(this.elementId);
            bfBody[DomAttribute.GRIDPK] = "";
        }

        const overflow = this.state.wrappedCompState.overflow;
        const modalBodyStyle = ComponentHelper.makeOverflowStyle(overflow);
        const body = !window[WindowKey.DESIGNMODE] && !window[WindowKey.VERSIONMODE] ? "bf-modal-body" : "'";
        const grid = this.state.wrappedCompState.gridId ? GridHelper.infoGridContainer(this.parentDom, this.state.wrappedCompState.gridId, "Grid Detail Modal Container", null, this.state[GridLinkIdKeyConstant]) : null;
        return (
            <React.Fragment>
                {grid}
                <div ref={this._setRef} className="bf-modal-content" style={modalWindowStyle} {...bfContent}>
                    {this._createHeader()}
                    {this._designBody()}
                    <div className={body} style={modalBodyStyle} {...bfBody}>
                    </div>
                    {this._createFooter()}
                </div>
            </React.Fragment>
        );
    }

    protected _createHeader(): React.ReactNode {
        const { title, useCommand } = this.state.wrappedCompState;
        if (useCommand) {
            const bfHeader = {[DomAttribute.MODALHEADER]: ""};
            return (
                <div className="bf-modal-header" {...bfHeader}>
                    <h1 className="modal-title">{ title }</h1>
                    <div className="icon">
                        {this._createCommandIcons()}
                    </div>
                </div>
            );
        } else {
            return null;
        }
    }

    protected _filterWindowIconsByState(o): boolean {
        const windowState = this.state.wrappedCompState.windowState;
        let rtn = true;

        if (windowState && o && "label" in o) {
            switch (o.label) {
                case OptionalWindowCommandState.MINIMIZE:
                    rtn = (windowState !== OptionalWindowCommandState.WINDOW
                        && windowState !== OptionalWindowCommandState.MINIMIZE);
                    break;
                case OptionalWindowCommandState.MAXIMIZE:
                    rtn = (windowState !== OptionalWindowCommandState.WINDOW
                        && windowState === OptionalWindowCommandState.MINIMIZE);
                    break;
                case OptionalWindowCommandState.DEFAULT:
                    rtn = (windowState !== OptionalWindowCommandState.WINDOW
                        && windowState !== OptionalWindowCommandState.DEFAULT);
                    break;
                case OptionalWindowCommandState.DIALOG:
                    rtn = (windowState === OptionalWindowCommandState.WINDOW);
                    break;
                case OptionalWindowCommandState.WINDOW:
                    rtn = (windowState !== OptionalWindowCommandState.WINDOW);
                    break;
            }
        }

        return rtn;
    }

    protected _createCommandIcons(): React.ReactNode {
        const model = this.state.wrappedCompState.model;
        const filteredCommandIcons = _.concat(_.filter(this._internalModel.value, this._filterWindowIconsByState), model);
        return filteredCommandIcons.map(this._icon);
    }

    protected _icon(item: any, index: number) {
        if (item && item.hasOwnProperty("icon")) {
            const Attribute = item.icon === "fas fa-times" ? {
                [DomAttribute.MODALCLOSE] : ""
            } : null;

            return (
                <div
                    {...Attribute}
                    key={index}
                    className="m-0 p-0"
                    style={{cursor: "pointer"}}
                    tabIndex={1}
                    aria-label={I18N.translate("{$I._app.accessibility.component.modal.doClose}", {title: this.state.wrappedCompState.title})}
                    role="button"
                    onClick={this._commandClick}
                    onKeyDown={this._commandEnter}
                >
                    <i
                        onClick={this._commandClick}
                        aria-hidden="true" className={item["icon"]}
                    ></i>
                </div>
            );
        }
    }

    protected _commandClick(e: any): void {
        ModalHandler.clickCloseButton(e.target as HTMLElement);
        e.stopPropagation();
        e.preventDefault();
    }

    protected _commandEnter(e: any): void {
        if (e && e.key === "Enter") {
            ModalHandler.clickCloseButton(e.target as HTMLElement);
            e.stopPropagation();
            e.preventDefault();
        }
    }

    protected _createFooter(): React.ReactNode {
        if (this.state.wrappedCompState.footer) {
            const bfAction = {
                [DomAttribute.MODALFOOTER]: "",
                [DomAttribute.CONTAINER]: "bf-action-container"
            };
            if (this.state.wrappedCompState[GridIdKeyConstant]) {
                bfAction[DomAttribute.GRIDACTIONCONTAINER] = StringHelper.elementId(this.elementId);
                bfAction[DomAttribute.GRIDPK] = "";
            }
            const footer = !window[WindowKey.DESIGNMODE] && !window[WindowKey.VERSIONMODE] ? "bf-modal-footer" : "";
            return (
                <React.Fragment>
                    {this._designFooter()}
                    <div className={footer} {...bfAction}></div>
                </React.Fragment>
            );
        }
        return null;
    }

    protected _designBody(): React.ReactNode {
        return window[WindowKey.DESIGNMODE] ? <div>Modal Body</div> : null;
    }

    protected _designFooter(): React.ReactNode {
        return window[WindowKey.DESIGNMODE] ? <div style={{marginTop: "30px"}}>Modal Footer</div> : null;
    }

    protected _setRef(ref): void {
        if (ref) {
            this._ref = ref;
            this.readyComponent();
        }
    }
}
